import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { DsButton } from "../../../../design-system";
import {
  MEAL_VOUCHER_MAX_PER_MEAL,
  NPS_RATE_MODES,
  VPF_RATE_MODES,
  MEAL_RATE_MODES,
  VPF_MAX_PERCENT,
  mealVoucherMonthlyCap,
  normalizeNpsConfig,
  normalizeVpfConfig,
  normalizeMealVoucherConfig,
  validateVpfEmployeeValues,
  validateMealVoucherMonthly,
} from "../../../../utils/salaryStructureAddons";
import { PB_FREQUENCIES } from "../../../../utils/salaryStructureComponentRules";

function todayYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function ymToEffectiveFrom(ym) {
  const s = String(ym || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(s)) return new Date().toISOString().slice(0, 10);
  return `${s}-01`;
}

/**
 * Detail popup for structure/employee add-on configuration (NPS / VPF / Meal / PB).
 * Structure level always asks Effective from (same idea as component Status from).
 * Employee level uses requireEffectiveMonth + month-lock validation.
 */
export default function StructureAddonConfigModal({
  isOpen,
  onClose,
  title,
  addonKey, // nps | vpf | mealVoucher | statutory | performanceBonus
  level = "structure", // structure | employee
  initialConfig = {},
  structureDefaults = null, // when employee level, structure defaults for flat mode
  pfApplicable = true,
  readOnly = false,
  requireEffectiveMonth = false,
  minEffectiveMonth = null, // YYYY-MM — next allowed after finalised / company min
  infoText = null,
  onSave,
}) {
  const [config, setConfig] = useState(initialConfig);
  const [effectiveMonth, setEffectiveMonth] = useState(todayYm());
  const [saving, setSaving] = useState(false);

  const needsEffectiveMonth =
    requireEffectiveMonth || level === "structure" || addonKey === "statutory";

  useEffect(() => {
    if (!isOpen) return;
    if (addonKey === "nps") setConfig(normalizeNpsConfig(initialConfig));
    else if (addonKey === "vpf") setConfig(normalizeVpfConfig(initialConfig));
    else if (addonKey === "mealVoucher") setConfig(normalizeMealVoucherConfig(initialConfig));
    else if (addonKey === "performanceBonus") {
      setConfig({
        enabled: true,
        amount: initialConfig?.amount ?? initialConfig?.performanceBonusAmount ?? "",
        period: initialConfig?.period || initialConfig?.performanceBonusPeriod || "annual",
        frequency: initialConfig?.frequency || "annual",
        effectiveFrom: initialConfig?.effectiveFrom || "",
      });
    } else setConfig({ ...(initialConfig || {}) });
    const existing =
      String(initialConfig?.effectiveFrom || initialConfig?.effectiveMonth || "").slice(0, 7);
    const seed = /^\d{4}-\d{2}$/.test(existing) ? existing : todayYm();
    const clamped =
      minEffectiveMonth && seed < String(minEffectiveMonth) ? String(minEffectiveMonth) : seed;
    setEffectiveMonth(clamped);
  }, [isOpen, addonKey, initialConfig, minEffectiveMonth]);

  const mealCap = useMemo(
    () => (addonKey === "mealVoucher" ? mealVoucherMonthlyCap(config) : 0),
    [addonKey, config]
  );

  if (!isOpen) return null;

  const handleSave = async () => {
    if (readOnly) {
      onClose?.();
      return;
    }
    if (addonKey === "performanceBonus" && level === "employee") {
      const amt = Number(config.amount ?? config.performanceBonusAmount);
      if (!Number.isFinite(amt) || amt <= 0) {
        toast.error("Enter a PB amount greater than zero");
        return;
      }
    }
    if (addonKey === "vpf" && level === "employee" && !pfApplicable) {
      toast.error("VPF is allowed only when PF Applicable is checked");
      return;
    }
    if (addonKey === "vpf") {
      const err = validateVpfEmployeeValues({
        percent: config.flatPercent ?? config.percent,
        amount: config.flatAmount ?? config.amount,
        rateMode: config.rateMode,
      });
      if (err) {
        toast.error(err);
        return;
      }
    }
    if (addonKey === "mealVoucher") {
      const structureMode =
        level === "employee"
          ? String(structureDefaults?.rateMode || "flat").toLowerCase()
          : String(config.rateMode || "flat").toLowerCase();
      const monthly =
        structureMode === "flat"
          ? Number(config.flatMonthlyAmount ?? structureDefaults?.flatMonthlyAmount)
          : Number(config.monthlyAmount ?? config.flatMonthlyAmount);
      const perMeal = Number(config.amountPerMeal) || 0;
      const meals = Number(config.mealsPerDay) || 0;
      const hasDerived = structureMode === "employee" && perMeal > 0 && meals > 0;
      if (structureMode === "employee" && !(monthly > 0) && !hasDerived) {
        toast.error("Enter Monthly meal voucher amount (or Amount/meal × Meals/day)");
        return;
      }
      if (structureMode === "flat" && !(monthly > 0) && level === "structure") {
        toast.error("Enter Flat monthly amount for Meal Voucher");
        return;
      }
      const warn = validateMealVoucherMonthly(monthly > 0 ? monthly : perMeal * meals * 22, config);
      if (warn && Number(monthly > 0 ? monthly : perMeal * meals * 22) > mealCap * 1.5) {
        toast.error(warn);
        return;
      }
      if (warn) toast(warn, { icon: "⚠️" });
    }
    if (needsEffectiveMonth) {
      if (!/^\d{4}-\d{2}$/.test(String(effectiveMonth || ""))) {
        toast.error("Select an effective month");
        return;
      }
      if (minEffectiveMonth && String(effectiveMonth) < String(minEffectiveMonth)) {
        toast.error(
          `Effective month cannot be on or before finalised/processed payroll (${minEffectiveMonth}). Choose a later month.`
        );
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...config,
        enabled: config.enabled !== false,
        ...(needsEffectiveMonth
          ? { effectiveFrom: ymToEffectiveFrom(effectiveMonth), effectiveMonth }
          : {}),
      };
      await onSave?.(payload);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-4"
      onClick={() => !saving && onClose?.()}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-[hsl(var(--ds-fg))]">{title}</h3>
            <p className="mt-0.5 text-xs text-[hsl(var(--ds-muted-fg))]">
              {level === "structure"
                ? "Structure defaults for employees under this salary structure"
                : "Employee-level options (only when structure enables this feature)"}
            </p>
          </div>
          <button type="button" className="rounded-full p-1 opacity-70 hover:opacity-100" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4 max-h-[70vh] overflow-y-auto">
          {infoText ? (
            <p className="rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 px-3 py-2 text-xs text-[hsl(var(--ds-muted-fg))]">
              {infoText}
            </p>
          ) : null}

          {addonKey === "statutory" && !infoText && (
            <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
              Saves PF / Ceiling PF / EPS / ESI / PT / Gratuity with an effective month (same rules as Update
              Statutory). After payroll is processed or finalised for a month, choose a later month.
            </p>
          )}

          {addonKey === "performanceBonus" && (
            <>
              {!infoText ? (
                <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                  Total package includes Fixed + PB. Component breakdown uses Fixed only (Total − PB).
                  Payout % is asked when PB is paid in payroll.
                </p>
              ) : null}
              {level === "employee" ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium">PB amount</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      disabled={readOnly}
                      value={config.amount ?? config.performanceBonusAmount ?? ""}
                      onChange={(e) => setConfig((c) => ({ ...c, amount: e.target.value }))}
                      className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                      placeholder="e.g. 300000"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">PB period</label>
                    <select
                      disabled={readOnly}
                      value={config.period || "annual"}
                      onChange={(e) => setConfig((c) => ({ ...c, period: e.target.value }))}
                      className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                    >
                      <option value="annual">Annual</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium">Payout frequency</label>
                    <select
                      disabled={readOnly}
                      value={config.frequency || "annual"}
                      onChange={(e) => setConfig((c) => ({ ...c, frequency: e.target.value }))}
                      className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                    >
                      {PB_FREQUENCIES.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="mb-1 block text-sm font-medium">Payout frequency</label>
                  <select
                    disabled={readOnly}
                    value={config.frequency || "annual"}
                    onChange={(e) => setConfig((c) => ({ ...c, frequency: e.target.value }))}
                    className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                  >
                    {PB_FREQUENCIES.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {addonKey === "nps" && (
            <>
              <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                Employer NPS is a <strong>Benefit</strong> (like PF Employer), mapped to TDS{" "}
                <strong>80CCD(2)</strong>. Counts toward the ₹7.5L employer PF+NPS+SA perquisite cap.
              </p>
              {level === "structure" ? (
                <>
                  <label className="block text-sm font-medium">Rate mode</label>
                  <select
                    disabled={readOnly}
                    value={config.rateMode || "flat"}
                    onChange={(e) => setConfig((c) => ({ ...c, rateMode: e.target.value }))}
                    className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                  >
                    {NPS_RATE_MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  {config.rateMode === "flat" && (
                    <div>
                      <label className="mb-1 block text-sm font-medium">Flat % of wage</label>
                      <input
                        type="number"
                        disabled={readOnly}
                        min={0}
                        max={14}
                        step={0.01}
                        value={config.flatPercent ?? 10}
                        onChange={(e) =>
                          setConfig((c) => ({ ...c, flatPercent: Number(e.target.value) }))
                        }
                        className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                      />
                      <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                        Typical private-sector 80CCD(2) cap is 10% of salary (Basic+DA); New regime up to 14%.
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="mb-1 block text-sm font-medium">Wage basis</label>
                    <select
                      disabled={readOnly}
                      value={config.wageBasis || "basic"}
                      onChange={(e) => setConfig((c) => ({ ...c, wageBasis: e.target.value }))}
                      className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                    >
                      <option value="basic">Basic</option>
                      <option value="pf_wage">PF wage</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  {structureDefaults?.rateMode === "flat" ? (
                    <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
                      Structure uses flat {structureDefaults.flatPercent}% — applied when opted in.
                    </p>
                  ) : (
                    <div>
                      <label className="mb-1 block text-sm font-medium">Employee NPS %</label>
                      <input
                        type="number"
                        disabled={readOnly}
                        min={0}
                        max={14}
                        step={0.01}
                        value={config.percent ?? config.flatPercent ?? ""}
                        onChange={(e) =>
                          setConfig((c) => ({ ...c, percent: Number(e.target.value) }))
                        }
                        className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {addonKey === "vpf" && (
            <>
              <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                VPF is an employee deduction under <strong>80C</strong> (with Emp PF, Old regime ₹1.5L).
                Allowed only when PF Applicable. Max {VPF_MAX_PERCENT}% so PF 12% + VPF ≤ 100% of PF wage.
              </p>
              {!pfApplicable && level === "employee" ? (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                  Enable PF Applicable first to opt into VPF.
                </p>
              ) : null}
              {level === "structure" ? (
                <>
                  <label className="block text-sm font-medium">Rate mode</label>
                  <select
                    disabled={readOnly}
                    value={config.rateMode || "flat_percent"}
                    onChange={(e) => setConfig((c) => ({ ...c, rateMode: e.target.value }))}
                    className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                  >
                    {VPF_RATE_MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  {config.rateMode === "flat_percent" && (
                    <div>
                      <label className="mb-1 block text-sm font-medium">Flat VPF %</label>
                      <input
                        type="number"
                        disabled={readOnly}
                        min={0}
                        max={VPF_MAX_PERCENT}
                        step={0.01}
                        value={config.flatPercent ?? 0}
                        onChange={(e) =>
                          setConfig((c) => ({ ...c, flatPercent: Number(e.target.value) }))
                        }
                        className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                  {config.rateMode === "flat_amount" && (
                    <div>
                      <label className="mb-1 block text-sm font-medium">Flat VPF amount (₹ / month)</label>
                      <input
                        type="number"
                        disabled={readOnly}
                        min={0}
                        step={1}
                        value={config.flatAmount ?? 0}
                        onChange={(e) =>
                          setConfig((c) => ({ ...c, flatAmount: Number(e.target.value) }))
                        }
                        className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  {structureDefaults?.rateMode === "flat_percent" ||
                  structureDefaults?.rateMode === "flat_amount" ? (
                    <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
                      Structure default:{" "}
                      {structureDefaults.rateMode === "flat_amount"
                        ? `₹${Number(structureDefaults.flatAmount || 0).toLocaleString("en-IN")}/mo`
                        : `${structureDefaults.flatPercent}%`}
                    </p>
                  ) : (
                    <>
                      <label className="block text-sm font-medium">Employee VPF mode</label>
                      <select
                        disabled={readOnly || !pfApplicable}
                        value={config.rateMode || "flat_percent"}
                        onChange={(e) => setConfig((c) => ({ ...c, rateMode: e.target.value }))}
                        className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                      >
                        <option value="flat_percent">%</option>
                        <option value="flat_amount">Amount (₹)</option>
                      </select>
                      {(config.rateMode || "flat_percent") === "flat_percent" ? (
                        <div>
                          <label className="mb-1 block text-sm font-medium">VPF %</label>
                          <input
                            type="number"
                            disabled={readOnly || !pfApplicable}
                            min={0}
                            max={VPF_MAX_PERCENT}
                            value={config.percent ?? ""}
                            onChange={(e) =>
                              setConfig((c) => ({ ...c, percent: Number(e.target.value) }))
                            }
                            className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="mb-1 block text-sm font-medium">VPF amount (₹ / month)</label>
                          <input
                            type="number"
                            disabled={readOnly || !pfApplicable}
                            min={0}
                            value={config.amount ?? ""}
                            onChange={(e) =>
                              setConfig((c) => ({ ...c, amount: Number(e.target.value) }))
                            }
                            className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                          />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {addonKey === "mealVoucher" && (
            <>
              <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                Meal vouchers: tax-free up to <strong>₹{MEAL_VOUCHER_MAX_PER_MEAL} per meal</strong>{" "}
                (Income-Tax Rules 2026, from Apr 2026), restricted-use vouchers during working hours.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">₹ per meal (max {MEAL_VOUCHER_MAX_PER_MEAL})</label>
                  <input
                    type="number"
                    disabled={readOnly}
                    min={0}
                    max={MEAL_VOUCHER_MAX_PER_MEAL}
                    value={config.amountPerMeal ?? MEAL_VOUCHER_MAX_PER_MEAL}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        amountPerMeal: Math.min(Number(e.target.value) || 0, MEAL_VOUCHER_MAX_PER_MEAL),
                      }))
                    }
                    className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Meals / day</label>
                  <input
                    type="number"
                    disabled={readOnly}
                    min={1}
                    max={3}
                    value={config.mealsPerDay ?? 2}
                    onChange={(e) =>
                      setConfig((c) => ({ ...c, mealsPerDay: Number(e.target.value) || 1 }))
                    }
                    className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Soft monthly exempt hint (~22 days): ₹{mealCap.toLocaleString("en-IN")}
              </p>
              {level === "structure" ? (
                <>
                  <label className="block text-sm font-medium">Amount mode</label>
                  <select
                    disabled={readOnly}
                    value={config.rateMode || "flat"}
                    onChange={(e) => setConfig((c) => ({ ...c, rateMode: e.target.value }))}
                    className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                  >
                    {MEAL_RATE_MODES.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  {config.rateMode === "flat" && (
                    <div>
                      <label className="mb-1 block text-sm font-medium">Flat monthly amount (₹)</label>
                      <input
                        type="number"
                        disabled={readOnly}
                        min={0}
                        value={config.flatMonthlyAmount ?? 0}
                        onChange={(e) =>
                          setConfig((c) => ({ ...c, flatMonthlyAmount: Number(e.target.value) }))
                        }
                        className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </>
              ) : structureDefaults?.rateMode === "flat" ? (
                <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
                  Structure flat monthly: ₹
                  {Number(structureDefaults.flatMonthlyAmount || 0).toLocaleString("en-IN")}
                </p>
              ) : (
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    Monthly meal voucher (₹) — optional if ₹/meal × meals/day set
                  </label>
                  <input
                    type="number"
                    disabled={readOnly}
                    min={0}
                    value={config.monthlyAmount ?? ""}
                    onChange={(e) =>
                      setConfig((c) => ({ ...c, monthlyAmount: Number(e.target.value) }))
                    }
                    className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                    If blank, payroll uses ₹/meal × meals/day × calendar days in the pay month.
                  </p>
                </div>
              )}
            </>
          )}

          {needsEffectiveMonth && (
            <div>
              <label className="mb-1 block text-sm font-medium">Effective from</label>
              <input
                type="month"
                disabled={readOnly}
                value={effectiveMonth}
                min={minEffectiveMonth || undefined}
                onChange={(e) => setEffectiveMonth(e.target.value)}
                className="w-full rounded-md border border-[hsl(var(--ds-border))] px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Same as component Status from — this option applies from the selected month.
              </p>
              {minEffectiveMonth ? (
                <p className="mt-1 text-[11px] text-[hsl(var(--ds-warning))]">
                  Locked through {minEffectiveMonth}. Choose a later month (or unprocess/unfinalise first).
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
          <DsButton type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
            {readOnly ? "Close" : "Cancel"}
          </DsButton>
          {!readOnly && (
            <DsButton type="button" variant="primary" size="sm" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </DsButton>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
